import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';

const MusicPlayer = () => {
  // 預設為播放狀態
  const [isPlaying, setIsPlaying] = useState(true);
  const audioRef = useRef(null);
  const buttonRef = useRef(null);
  const hasInteractedRef = useRef(false);
  const wasPlayingRef = useRef(true); // 記錄暫停前的播放狀態
  
  // 檢查音頻實際播放狀態並更新UI
  const updateUIBasedOnActualPlayback = () => {
    if (audioRef.current) {
      const actuallyPlaying = !audioRef.current.paused;
      if (isPlaying !== actuallyPlaying) {
        setIsPlaying(actuallyPlaying);
        localStorage.setItem('bgMusicState', actuallyPlaying ? 'playing' : 'paused');
      }
    }
  };
  
  // 初始化音樂播放器
  useEffect(() => {
    // 總是將本地存儲中的音樂狀態設置為播放
    localStorage.setItem('bgMusicState', 'playing');
    
    // 設置為播放狀態
    setIsPlaying(true);
    wasPlayingRef.current = true;
    
    // 添加用戶交互監聽，以便在首次交互後播放音樂
    const handleUserInteraction = () => {
      if (!hasInteractedRef.current) {
        audioRef.current.play().catch(err => {
          console.log('自動播放受限:', err);
          // 即使播放失敗，還是標記為已交互，這樣下次有機會時會嘗試播放
          hasInteractedRef.current = true;
        });
        hasInteractedRef.current = true;
      }
      
      // 移除事件監聽器，避免重複觸發
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
    
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
    
    // 添加音頻事件監聽器來確保UI狀態與實際播放狀態同步
    const handleAudioPlay = () => {
      setIsPlaying(true);
      localStorage.setItem('bgMusicState', 'playing');
    };
    
    const handleAudioPause = () => {
      setIsPlaying(false);
      localStorage.setItem('bgMusicState', 'paused');
    };
    
    if (audioRef.current) {
      audioRef.current.addEventListener('play', handleAudioPlay);
      audioRef.current.addEventListener('pause', handleAudioPause);
    }
    
    // 設置全局函數以供其他組件控制背景音樂
    window.pauseBackgroundMusic = () => {
      if (audioRef.current && !audioRef.current.paused) {
        wasPlayingRef.current = true;
        audioRef.current.pause();
      }
    };
    
    window.resumeBackgroundMusic = () => {
      // 總是嘗試播放，不管之前的狀態如何
      if (audioRef.current && hasInteractedRef.current) {
        audioRef.current.play().catch(err => {
          console.log('恢復播放受限:', err);
        });
        setIsPlaying(true);
        localStorage.setItem('bgMusicState', 'playing');
      }
    };
    
    // 每秒檢查一次播放狀態以確保UI與實際播放狀態同步
    const checkInterval = setInterval(updateUIBasedOnActualPlayback, 1000);
    
    // 頁面載入後自動嘗試播放
    const autoPlayTimeout = setTimeout(() => {
      if (hasInteractedRef.current && audioRef.current && audioRef.current.paused) {
        audioRef.current.play().catch(err => {
          console.log('自動播放受限:', err);
        });
      }
    }, 1000);
    
    // 清理函數
    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      
      if (audioRef.current) {
        audioRef.current.removeEventListener('play', handleAudioPlay);
        audioRef.current.removeEventListener('pause', handleAudioPause);
      }
      
      clearInterval(checkInterval);
      clearTimeout(autoPlayTimeout);
      delete window.pauseBackgroundMusic;
      delete window.resumeBackgroundMusic;
    };
  }, []);
  
  // 監聽播放狀態變化 (本機和 localStorage)
  useEffect(() => {
    if (hasInteractedRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(err => {
          console.log('播放受限:', err);
          setIsPlaying(false);
          localStorage.setItem('bgMusicState', 'paused');
        });
      } else {
        audioRef.current.pause();
      }
    }
    
    wasPlayingRef.current = isPlaying;
  }, [isPlaying]);
  
  // 處理頁面/應用可見性變化和離開事件
  useEffect(() => {
    // 暫停音樂
    const pauseMusic = () => {
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
    };
    
    // 恢復音樂
    const resumeMusic = () => {
      // 總是嘗試恢復播放，不管之前的狀態如何
      if (audioRef.current && audioRef.current.paused && hasInteractedRef.current) {
        audioRef.current.play().catch(err => {
          console.log('恢復播放受限:', err);
        });
        setIsPlaying(true);
        localStorage.setItem('bgMusicState', 'playing');
      }
    };
    
    // 頁面可見性改變事件
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        pauseMusic();
      } else if (document.visibilityState === 'visible') {
        resumeMusic();
        // 確保頁面返回時更新播放狀態UI
        setTimeout(updateUIBasedOnActualPlayback, 300);
      }
    };
    
    // 頁面隱藏事件（適用於移動裝置退出瀏覽器）
    const handlePageHide = () => {
      pauseMusic();
    };
    
    // 頁面顯示事件
    const handlePageShow = (e) => {
      resumeMusic();
      // 確保頁面返回時更新播放狀態UI
      setTimeout(updateUIBasedOnActualPlayback, 300);
    };
    
    // 視窗失去焦點事件
    const handleBlur = () => {
      pauseMusic();
    };
    
    // 視窗獲得焦點事件
    const handleFocus = () => {
      resumeMusic();
      // 確保獲得焦點時更新播放狀態UI
      setTimeout(updateUIBasedOnActualPlayback, 300);
    };
    
    // 監聽各種相關事件
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    
    // 行動裝置特定事件
    if ('onfreeze' in document) {
      document.addEventListener('freeze', pauseMusic);
      document.addEventListener('resume', resumeMusic);
    }
    
    // 清理函數
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      
      if ('onfreeze' in document) {
        document.removeEventListener('freeze', pauseMusic);
        document.removeEventListener('resume', resumeMusic);
      }
    };
  }, []);
  
  // 切換音樂播放狀態
  const toggleMusic = () => {
    const newState = !isPlaying;
    setIsPlaying(newState);
    localStorage.setItem('bgMusicState', newState ? 'playing' : 'paused');
    
    // 確保用戶已交互
    hasInteractedRef.current = true;
    
    // 按鈕動畫 - 只放大縮小，不旋轉
    gsap.to(buttonRef.current, {
      scale: 1.2,
      duration: 0.3,
      ease: "back.out",
      onComplete: () => {
        gsap.to(buttonRef.current, {
          scale: 1,
          duration: 0.2
        });
      }
    });
  };
  
  return (
    <div className="music-player">
      <audio 
        ref={audioRef} 
        src="/assets/sounds/background-music.mp3" 
        loop={true}
      />
      <button 
        ref={buttonRef}
        className={`music-toggle ${isPlaying ? 'playing' : 'paused'}`} 
        onClick={toggleMusic}
        aria-label={isPlaying ? "關閉背景音樂" : "開啟背景音樂"}
      >
        {isPlaying ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.5 12C14.5 10.23 13.48 8.71 12 7.97V16.02C13.48 15.29 14.5 13.77 14.5 12Z" />
            <path d="M3 9V15H7L12 20V4L7 9H3ZM16.5 12C16.5 8.97 14.65 6.32 12 5.36V2.5C16.15 3.54 19 7.4 19 12C19 16.6 16.15 20.46 12 21.5V18.64C14.65 17.68 16.5 15.03 16.5 12Z" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.34 2.93L2.93 4.34L7.29 8.7L7 9H3V15H7L12 20V13.41L16.18 17.59C15.69 17.97 15.16 18.29 14.58 18.53V21.07C15.85 20.67 17.0 19.9 17.97 18.9L19.66 20.59L21.07 19.18L4.34 2.93ZM10 15.17L7.83 13H5V11H7.83L10 8.83V15.17ZM12 4L9.91 6.09L12 8.18V4ZM16.5 12C16.5 10.23 15.5 8.71 14 7.97V10.18L16.45 12.63C16.48 12.43 16.5 12.22 16.5 12Z" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default MusicPlayer;